import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { getCookie } from "../commonfunction";
import {store} from "../../Redux/Store/Store";
import { redactErrorForLog } from "./redactError";

// Store cancel token sources for managing concurrent requests

// axios.defaults.withCredentials = true;

const cancelTokenSources = {};

// Base URL for the API (from environment variables)
/**
 * Function to generate the full API URL based on the route.
 * @param {string} route - API endpoint route.
 * @returns {string} - Full API URL.
 */

/**
 * Handles request errors.
 * @param {Object} error - The error object from the request.
 */
const handleRequestError = (error) => {
  const isRequestCancelled = axios.isCancel(error);
  error.isRequestCancelled = isRequestCancelled;
  if (isRequestCancelled) {
    console.error("Request canceled:", error.message);
  } else {
    console.error("Error making request:", redactErrorForLog(error));
  }

  return error;
};


const handleHeader = (actualHeader) => {
  const accessToken = getCookie("accessToken");
  const header = accessToken
    ? {
      Authorization: `Bearer ${accessToken}`,
    }
    : {};
  return {
    ...header,
    ...actualHeader,
  };
}

const DefaultValueAdded = (data) => {
  const sessionId = store.getState()?.Patientdetails?.sessionId;
  if ((window?.location?.pathname||'')?.includes('/admin')) {
    return data
  }
  return {
    sessionId,
    path: window?.location?.href,
    ...data,
  };
};

/**
 * API GET request.
 * @param {string} route - The API endpoint.
 * @param {Object} [params={}] - Query parameters.
 * @param {string|null} [methodKey=null] - Unique key to identify the request.
 * @returns {Promise<Object>} - The response from the API.
 */
export const apiGet = async (
  route,
  params = {},
  methodKey = null,
  headers = {}
) => {

  try {
    if (!methodKey) methodKey = uuidv4();

    // Cancel previous request for this method, if any
    if (cancelTokenSources[methodKey]) {
      cancelTokenSources[methodKey].cancel(
        "Request canceled: New request made."
      );
    }

    // Create a new cancel token source for this method
    cancelTokenSources[methodKey] = axios.CancelToken.source();
    const response = await axios.get(route, {
      params: DefaultValueAdded(params),
      cancelToken: cancelTokenSources[methodKey].token,
      headers: handleHeader(headers),
    });

    return response.data; // Return response data
  } catch (error) {
    handleRequestError(error);
    throw error;
  }
};

/**
 * API POST request.
 * @param {string} route - The API endpoint.
 * @param {Object} data - The data to send with the request.
 * @param {string|null} [methodKey=null] - Unique key to identify the request.
 * @param {Object} [header={}] - Additional headers for the request.
 * @param {function} [onUploadProgress=null] - Progress callback for tracking uploads.
 * @returns {Promise<Object>} - The response from the API.
 */
export const apiPost = async (
  route,
  data,
  methodKey = null,
  headers = {},
  onUploadProgress = null
) => {

  try {
    if (!methodKey) methodKey = uuidv4();

    // Cancel previous request for this method, if any
    if (cancelTokenSources[methodKey]) {
      cancelTokenSources[methodKey].cancel(
        "Request canceled: New request made."
      );
    }

    // Create a new cancel token source for this method
    cancelTokenSources[methodKey] = axios.CancelToken.source();

    const response = await axios.post(route, DefaultValueAdded(data), {
      cancelToken: cancelTokenSources[methodKey].token,
      headers: handleHeader(headers),
      onUploadProgress,
    });

    return response.data; // Return response data
  } catch (error) {
    handleRequestError(error);
    throw error;
  }
};

/**
 * API PUT request.
 * @param {string} route - The API endpoint.
 * @param {Object} data - The data to send with the request.
 * @param {string|null} [methodKey=null] - Unique key to identify the request.
 * @returns {Promise<Object>} - The response from the API.
 */
export const apiPut = async (route, data, methodKey = null, headers = {}) => {

  try {
    if (!methodKey) methodKey = uuidv4();

    // Cancel previous request for this method, if any
    if (cancelTokenSources[methodKey]) {
      cancelTokenSources[methodKey].cancel(
        "Request canceled: New request made."
      );
    }

    // Create a new cancel token source for this method
    cancelTokenSources[methodKey] = axios.CancelToken.source();

    const response = await axios.put(route, DefaultValueAdded(data), {
      cancelToken: cancelTokenSources[methodKey].token,
      headers: handleHeader(headers),
    });

    return response.data; // Return response data
  } catch (error) {
    handleRequestError(error);
    throw error;
  }
};

/**
 * API DELETE request.
 * @param {string} route - The API endpoint.
 * @param {string|null} [methodKey=null] - Unique key to identify the request.
 * @returns {Promise<Object>} - The response from the API.
 */
export const apiDelete = async (route,payload = {}, methodKey = null, headers = {}) => {
  try {

    if (!methodKey) methodKey = uuidv4();

    // Cancel previous request for this method, if any
    if (cancelTokenSources[methodKey]) {
      cancelTokenSources[methodKey].cancel(
        "Request canceled: New request made."
      );
    }

    // Create a new cancel token source for this method
    cancelTokenSources[methodKey] = axios.CancelToken.source();

    const response = await axios.delete(route, {
      cancelToken: cancelTokenSources[methodKey].token,
      headers: handleHeader(headers),
      data: DefaultValueAdded(payload),
    });

    return response.data; // Return response data
  } catch (error) {
    handleRequestError(error);
    throw error;
  }
};

/**
 * API PATCH request.
 * @param {string} route - The API endpoint.
 * @param {Object} data - The data to send with the request.
 * @param {string|null} [methodKey=null] - Unique key to identify the request.
 * @returns {Promise<Object>} - The response from the API.
 */
export const apiPatch = async (route, data, methodKey = null, headers = {}) => {
  try {

    if (!methodKey) methodKey = uuidv4();

    // Cancel previous request for this method, if any
    if (cancelTokenSources[methodKey]) {
      cancelTokenSources[methodKey].cancel(
        "Request canceled: New request made."
      );
    }

    // Create a new cancel token source for this method
    cancelTokenSources[methodKey] = axios.CancelToken.source();

    const response = await axios.patch(route, DefaultValueAdded(data), {
      cancelToken: cancelTokenSources[methodKey].token,
      headers: handleHeader(headers),
    });

    return response.data; // Return response data
  } catch (error) {
    handleRequestError(error);
    throw error;
  }
};
